import { Graph } from './graph'
import type { LineID, Vertex, VertexType } from './types'
import { EdgeType } from './types'

export interface StationInput {
  readonly id: string
  readonly x: number
  readonly y: number
  readonly type: VertexType
  readonly lines: readonly LineID[]
  /** Shared TfL identifier for cluster siblings — propagated onto every
   *  Vertex constructed from this station. Multiple per-line records of
   *  the same physical station carry the same `naptanId`; the routing
   *  algorithm uses it to detect cluster-revisits across distinct
   *  per-line `id`s and to dedup signature variants. */
  readonly naptanId?: string
}

export interface ConnectionInput {
  readonly id: string
  readonly lineID: LineID
  readonly fromID: string
  readonly toID: string
  readonly weightSec: number
  readonly reversible: boolean
}

export interface TransitionInput {
  readonly id: string
  readonly fromID: string
  readonly toID: string
  readonly fromLine: LineID
  readonly toLine: LineID
  readonly weightSec: number
  readonly reversible: boolean
  /** Schema `street: true` (1.27.0+) — pedestrian link between two
   *  different physical stations outside the gate line. Propagated
   *  onto the built edge; see `Edge.street`. */
  readonly street?: boolean
}

export interface GraphInputs {
  readonly stations: readonly StationInput[]
  readonly connections: readonly ConnectionInput[]
  readonly transitions: readonly TransitionInput[]
}

/** Builds a Graph from plain inputs. Pure: same inputs → same output. */
export function buildGraph(inputs: GraphInputs): Graph {
  const g = new Graph()
  const meta = new Map<string, { x: number; y: number; type: VertexType; naptanId?: string }>()
  for (const s of inputs.stations)
    meta.set(s.id, { x: s.x, y: s.y, type: s.type, naptanId: s.naptanId })

  const mkV = (id: string, line: LineID): Vertex => {
    const m = meta.get(id)
    return {
      id,
      x: m?.x ?? 0,
      y: m?.y ?? 0,
      type: m?.type ?? 'tube',
      lineID: line,
      naptanId: m?.naptanId,
    }
  }

  for (const c of inputs.connections) {
    const from = mkV(c.fromID, c.lineID),
      to = mkV(c.toID, c.lineID)
    g.addEdge({ from, to, weight: c.weightSec, type: EdgeType.Ride, realID: c.id })
    if (c.reversible)
      g.addEdge({ from: to, to: from, weight: c.weightSec, type: EdgeType.Ride, realID: c.id })
  }
  for (const t of inputs.transitions) {
    const from = mkV(t.fromID, t.fromLine),
      to = mkV(t.toID, t.toLine)
    // `street: true` marks an out-of-station link (tap out, walk, tap in);
    // every other transfer is an in-station interchange, whatever the
    // NaPTANs of its ends — v2 gives each mode its own (DLR Bank
    // `940GZZDLBNK`, Underground Bank `940GZZLUBNK`). Mirrors the server's
    // `routing.change` and the iOS / Android `GraphBuilder`.
    const eType: EdgeType =
      t.street === true ? EdgeType.WalkTransfer : EdgeType.WithinStationTransfer
    const street = t.street === true ? true : undefined
    g.addEdge({ from, to, weight: t.weightSec, type: eType, realID: t.id, street })
    if (t.reversible)
      g.addEdge({ from: to, to: from, weight: t.weightSec, type: eType, realID: t.id, street })
  }
  return g
}
